/// <amd-module name="Build/Run/RunView" />
import { IChecksPanelProps, IStageInfo, ReviewMode } from "Build/ApprovalsPanel/ChecksListPanel.props";
import { ApprovalUpdateParameters } from "Build/Client/Contracts/Approval";
import { Build, BuildDefinition, BuildReason, BuildResult, BuildStatus, DefinitionQueueStatus } from "Build/Client/Contracts/Build";
import { CheckListItemType, ProjectRetentionSettingData, RetentionLeaseData, RunDetailsData, RunStageData } from "Build/Client/Contracts/DataProvider";
import { ResourcePipelinePermissions } from "Build/Client/Contracts/PipelinePermissions";
import { CheckRunStatus, CheckSuite, CheckSuiteUpdateParameter, UpdateAction } from "Build/Client/Contracts/PipelinesChecks";
import { IBuildLinkingService } from "Build/Client/Linking";
import { IBuildRestClient } from "Build/Client/RestClient/Build";
import { IConfirmPipelineActionProps } from "Build/Common/Commands/ConfirmPipelineAction/Command.types";
import { IQueueBuildProps } from "Build/Common/Commands/QueueBuild/Command.types";
import { CommandComponent, prefetch } from "Build/Common/Library/Components/CommandComponent";
import { RetentionPanel, RetentionPanelProps } from "Build/Common/Library/Components/RetentionPanel";
import { ProcessType } from "Build/Common/Library/Constants";
import { BuildCallbackType, IBuildResultsViewExtensionConfig } from "Build/Common/Library/ContributedExtension.types";
import { handleError, registerErrorHandler } from "Build/Common/Library/ErrorHandler";
import { getBuildStatusIndicatorData } from "Build/Common/Library/Indicator";
import { IBuildNavigationService, openLinkSecurely } from "Build/Common/Library/Navigation";
import { formatPipelineRunName } from "Build/Common/Library/PipelineRunNameFormatter";
import { clearTreeStateOnUnmount } from "Build/Common/Library/StateHandler";
import { ContributionSource } from 'Build/Flux/Sources/Contribution';
import { ChecksCard, IStageCheckData } from "Build/Run/Components/ChecksCard";
import { IssuesCard } from "Build/Run/Components/IssuesCard";
import { JobsCard } from "Build/Run/Components/JobsCard";
import { NoData } from "Build/Run/Components/NoData";
import { RunSummary } from "Build/Run/Components/RunSummary";
import { SourcesCard } from "Build/Run/Components/SourcesCard";
import { TagsDialog } from "Build/Run/Components/TagsDialog";
import { CodeCoverageCiTabId, LeaseType, NavTestTabId, RetentionSettingDataContributionId, Telemetry } from "Build/Run/Constants";
import * as Resources from "Build/Run/Resources";
import { getRunDetailsService } from "Build/Run/RunDetailsService";
import { IRunDetailsPermissions, IRunDetailsService } from "Build/Run/RunDetailsService.types";
import { RunHeaderBreadcrumbService } from "Build/Run/RunHeaderBreadcrumbService";
import { IRunActionProps, IRunViewContributedTabProps, IRunViewStage } from "Build/Run/RunViewContributedTab";
import { RepositoryTypes } from "Build/SourceProviders/Common";
import { ISourceProviderService } from "Build/SourceProviders/SourceProvider";
import { GlobalMessageLevel, IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";
import { ITfsPageService } from "Tfs/Platform/Page";
import { IObservableArray, IObservableValue, ObservableArray, ObservableCollection, ObservableValue } from "VSS/Core/Observable";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { format } from "VSS/Core/Util/String";
import { HistoryBehavior, IPageStateHistoryService } from "VSS/Features/History";
import { ContributedIcon } from "VSS/Features/PlatformUI/ContributedIcon";
import { getMenuItems } from "VSS/Features/PlatformUI/ContributedMenu";
import { getTabs } from "VSS/Features/PlatformUI/ContributedTab";
import { onClickFPS } from "VSS/Platform/FPS";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssHistoryService } from "VSS/Platform/History";
import { Components, IVssComponentContext, IVssComponentProperties, IVssLayoutManager, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IVssPageService } from "VSS/Platform/Page";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { CustomHeader, HeaderBackButton, HeaderDescription, HeaderIcon, HeaderTitle, HeaderTitleArea, HeaderTitleRow, TitleSize } from "VSSUI/Header";
import { HeaderCommandBar, IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { IIconProps, IconSize } from "VSSUI/Icon";
import { IMenuItem, MenuItemType } from "VSSUI/Menu";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer, UncheckedObserver } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Pill, PillSize } from "VSSUI/Pill";
import { PillGroup, PillGroupOverflow } from "VSSUI/PillGroup";
import { Status, StatusSize } from "VSSUI/Status";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { IVssContributedTab, Tab, TabBar, TabContent, TabSize } from "VSSUI/Tabs";
import { Tooltip } from "VSSUI/TooltipEx";
import { css } from "VSSUI/Util";
import { ScreenSizeConditional } from "VSSUI/Utilities/ScreenSize";
import * as React from "react";

const c_cancelBuildAction = "nbr-cancel";
const c_retainBuildAction = "nbr-retain";
const c_deleteBuildAction = "nbr-delete";
const c_tagBuildAction = "nbr-tag";

/**
 * This is the original test tab Id from contribution
 */
const c_testTabId = "ms.vss-test-web.unified-test-results-tab";
/**
 * This should match "CodeCoverageCiTab/CommonUtils/Constants: PerfConstants.CodeCoverageCiTabId"
 */
const c_coverageCiTabPerfScenario = "Pipelines.CodeCoverage";

export interface IRunViewContentProps extends IVssComponentProperties {}

export interface IRunViewContentState {
    selectedTab: string;
    cancelClicked: boolean;
    showTagsDialog: ObservableValue<boolean>;
    runsToRetainPerProtectedBranch: string;
}

namespace Tabs {
    export const Results = "results";
}

namespace ArtifactResourceTypes {
    export let Container = "Container".toLowerCase();
    export const FilePath = "FilePath".toLowerCase();
}

export const legacyNoop = (args: any) => {
    console.error("Not supported");
};

class ContributedTabProps implements IRunViewContributedTabProps {
    public runId: ObservableValue<number>;
    public runNumber: ObservableValue<string>;
    public pipelineId: ObservableValue<number>;
    public projectId: ObservableValue<string>;
    public sourceBranch: ObservableValue<string>;
    public sourceRepositoryId: ObservableValue<string>;
    public sourceRepositoryType: ObservableValue<string>;
    public status: ObservableValue<BuildStatus>;
    public stages: ObservableArray<IRunViewStage>;
    public notifyUpdated: ObservableValue<number>;
    public sourceVersion: ObservableValue<string>;
    public finishTime: ObservableValue<Date>;
}

class RunViewContent extends VssComponent<IRunViewContentProps, IRunViewContentState> {
    private _run: IObservableValue<RunDetailsData>;
    private _service: IRunDetailsService;
    private _pivot: IObservableValue<string>;
    private _globalMessageService: IGlobalMessagesService;
    private _navigationService: IBuildNavigationService;
    private _tabProviders: ObservableCollection<IVssContributedTab> = new ObservableCollection<IVssContributedTab>();
    private _contributedTabProps: ContributedTabProps;
    private _performanceService: IVssPerformanceService;
    private _pageHistoryService: IPageStateHistoryService;
    private _projectId: string = "";

    private _resultsUrl: string;

    private _headerActions: IObservableArray<IHeaderCommandBarItem> = new ObservableArray<IHeaderCommandBarItem>();
    private _staticActions: IObservableArray<IHeaderCommandBarItem> = new ObservableArray<IHeaderCommandBarItem>();
    private _contributedActions: ObservableCollection<IHeaderCommandBarItem> = new ObservableCollection<IHeaderCommandBarItem>();

    private _extCallbacks: BuildCallbackType[] = [];
    private _showChecksPanel = new ObservableValue(false);
    private _checkListItemType: ObservableValue<CheckListItemType> = new ObservableValue(CheckListItemType.All);
    private _stagesToReview: ObservableArray<IStageInfo> = new ObservableArray();
    private _reviewMode: ObservableValue<ReviewMode> = new ObservableValue(ReviewMode.AllPending);

    private _activeElement?: HTMLElement;

    private _pendingStages: IStageInfo[];
    private _reviewingPendingStages = false;

    constructor(props: IRunViewContentProps, context: IVssComponentContext) {
        super(props, context);

        this._service = getRunDetailsService(this.context.pageContext);
        this._navigationService = this.context.pageContext.getService<IBuildNavigationService>("IBuildNavigationService");
        // TODO: update when we have a design for error display
        this._globalMessageService = this.context.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");

        this._pageHistoryService = this.context.pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");

        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        this._resultsUrl = historyService.generateUrl({ view: Tabs.Results });
        const currentState = historyService.getState();

        this._pivot = new ObservableValue((currentState && currentState["view"]) || Tabs.Results);
        this._run = this._service.data;

        const tfsPageService = context.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        if (tfsPageData && tfsPageData.project) {
            this._projectId = tfsPageData.project.id;
        }

        if (this._run) {
            this._contributedTabProps = {
                runId: new ObservableValue<number>(this._run.value.id),
                runNumber: new ObservableValue<string>(this._run.value.number),
                pipelineId: new ObservableValue<number>(this._run.value.pipeline.id),
                projectId: new ObservableValue<string>(this._projectId),
                sourceBranch: new ObservableValue<string>(this._run.value.sourceBranch),
                sourceRepositoryId: new ObservableValue<string>(this._run.value.sourceRepositoryId),
                sourceRepositoryType: new ObservableValue<string>(this._run.value.sourceRepositoryType),
                status: new ObservableValue<BuildStatus>(this._run.value.status),
                stages: new ObservableArray<IRunViewStage>(),
                notifyUpdated: new ObservableValue<number>(1),
                sourceVersion: new ObservableValue<string>(this._run.value.sourceVersion),
                finishTime: new ObservableValue<Date>(this._run.value.finishTime)
            };

            this._run.value.stages.forEach((stage: RunStageData) => {
                this._contributedTabProps.stages.push({ name: stage.name, refName: stage.refName });
            });
        }

        this._tabProviders.push(
            getTabs(context.pageContext, "ms.vss-build-web.run-details-view-tabs", {
                contextualData: this._contributedTabProps,
                props: this._contributedTabProps,
                preProcessTabContribution: this._updateTab
            })
        );

        this.state = {
            selectedTab: this._pivot.value,
            cancelClicked: false,
            showTagsDialog: new ObservableValue(false),
            runsToRetainPerProtectedBranch: ""
        };
        this._performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        this._initializeActions();
    }

    public render() {
        if (!this._run) {
            return <NoData />;
        }

        const useUpdatedChecksFlow = isFeatureFlagEnabled(this.context.pageContext, "Pipelines.Checks.EnableChecksScalabilityPhase1Flow", false)
        const isChecksLogsDisabled = isFeatureFlagEnabled(this.context.pageContext, "Pipelines.Checks.DisableUpdatedChecksLogs", false)
        const isAsyncCheckLogsFlowEnabled = isFeatureFlagEnabled(this.context.pageContext, "Pipelines.Checks.EnableUpdatedChecksLogsAsync", false)

        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="run-details-page" selectedTabId={this._pivot.value} tabProviders={this._tabProviders}>
                    <Observer run={this._run} leases={this._service.retentionLeases}>
                        {(observerProps: { run: RunDetailsData; leases: RetentionLeaseData[] }) => {
                            const title = formatPipelineRunName(
                                observerProps.run.number,
                                observerProps.run.sourceVersionMessage,
                                observerProps.run.appendCommitMessageToRunName
                            );
                            const subtitle = observerProps.run.pipeline.name;
                            return (
                                <>
                                    <CustomHeader className="run-view-header">
                                        <ScreenSizeConditional condition={size => size === ScreenSize.xsmall}>
                                            <HeaderBackButton
                                                buttonProps={{
                                                    onClick: this._onClickHeaderBack,
                                                    text: observerProps.run.pipeline.name
                                                }}
                                            />
                                        </ScreenSizeConditional>
                                        <div className="flex-row flex-self-stretch">
                                            <HeaderIcon iconProps={this._getHeaderIconProps(observerProps.run)} titleSize={TitleSize.Small} />
                                            <HeaderTitleArea className="padding-bottom-4">
                                                <HeaderTitleRow>
                                                    <div className="flex-row flex-baseline scroll-hidden">
                                                        <HeaderTitle titleSize={TitleSize.Large} className="text-ellipsis">
                                                            {title}
                                                        </HeaderTitle>
                                                    </div>
                                                </HeaderTitleRow>
                                                <ScreenSizeConditional condition={size => size > ScreenSize.xsmall}>
                                                    <div className="flex-row summary-line summary-line-non-link flex-center">
                                                        <ContributedIcon
                                                            alt=""
                                                            contributionId="ms.vss-build-web.pipelines-hub"
                                                            iconName="Build"
                                                            iconSize={IconSize.small}
                                                            pageContext={this.context.pageContext}
                                                            className="pipelines-icon-right-margin"
                                                        />
                                                        <HeaderDescription>{subtitle}</HeaderDescription>
                                                    </div>
                                                </ScreenSizeConditional>
                                            </HeaderTitleArea>
                                            <UncheckedObserver items={this._headerActions}>
                                                <HeaderCommandBar className="flex-self-start" items={[]} />
                                            </UncheckedObserver>
                                        </div>
                                        <ScreenSizeConditional condition={size => size > ScreenSize.xsmall}>
                                            {observerProps.run.tags.length > 0 && (
                                                <PillGroup
                                                    overflow={PillGroupOverflow.wrap}
                                                    className="margin-top-4 flex-self-stretch margin-left-32"
                                                >
                                                    {observerProps.run.tags.map(t => (
                                                        <Pill
                                                            className="margin-right-4"
                                                            key={t}
                                                            size={PillSize.compact}
                                                            excludeTabStop={true}
                                                            excludeFocusZone={true}
                                                        >
                                                            {t}
                                                        </Pill>
                                                    ))}
                                                </PillGroup>
                                            )}
                                        </ScreenSizeConditional>
                                    </CustomHeader>
                                    {(observerProps.run.finishTime || observerProps.leases.length > 0) && (
                                        <div className="page-content page-content-top">
                                            <MessageCard
                                                severity={
                                                    this._getMaxDaysUntilExpiration(observerProps.run, observerProps.leases) > 7
                                                        ? MessageCardSeverity.Info
                                                        : MessageCardSeverity.Warning
                                                }
                                                buttonProps={
                                                    observerProps.leases.length
                                                        ? [
                                                              {
                                                                  onClick: () => {
                                                                      this._onActivateRetentionPanel(true);
                                                                  },
                                                                  text: Resources.ViewRetentionLeasesMenuItem
                                                              }
                                                          ]
                                                        : []
                                                }
                                            >
                                                {this._getRetentionMessage(observerProps.run, observerProps.leases, this.state.runsToRetainPerProtectedBranch)}
                                            </MessageCard>
                                        </div>
                                    )}
                                </>
                            );
                        }}
                    </Observer>
                    <TabBar tabSize={TabSize.Tall} onSelectedTabChanged={this._onTabChanged}>
                        <Tab name={Resources.PipelineTabName} id={Tabs.Results} key={Tabs.Results} url={this._resultsUrl} />
                    </TabBar>
                    <div
                        className={css(
                            "run-details-tab-content page-content page-content-top",
                            this.state.selectedTab === NavTestTabId && "unified-testtab-extension-tab-content",
                            this.state.selectedTab !== Tabs.Results && "run-details-extension-tab-content"
                        )}
                    >
                        <TabContent>
                            {this.state.selectedTab === Tabs.Results && (
                                <Observer providers={this._tabProviders} run={this._run}>
                                    {(props: { providers: IVssContributedTab[]; run: RunDetailsData }) => {
                                        return (
                                            <div className="flex-column">
                                                <Observer
                                                    stages={this._stagesToReview}
                                                    shouldShowChecksPanel={this._showChecksPanel}
                                                    reviewMode={this._reviewMode}
                                                >
                                                    {(checkProps: {
                                                        stages: IStageInfo[];
                                                        shouldShowChecksPanel: boolean;
                                                        reviewMode: ReviewMode;
                                                    }) => {
                                                        const checkPanelProps: IChecksPanelProps = {
                                                            stages: checkProps.stages as IStageInfo[],
                                                            runId: props.run.id,
                                                            pipelineId: props.run.pipeline.id,
                                                            checkListItemType: this._checkListItemType.value,
                                                            reviewMode: checkProps.reviewMode,
                                                            onSubmitBulkApproval: this._onSubmitBulkApproval,
                                                            onReassignApprovals: this._onReassignApprovals,
                                                            onPermitResource: this._onPermitResource,
                                                            onDismiss: this._onPanelDismiss,
                                                            onViewStageClick: useUpdatedChecksFlow ? this._onViewStageChecksClicked: undefined,
                                                            onStageBackClick: (useUpdatedChecksFlow && this._reviewingPendingStages) ? this._onStageChecksBackClicked: undefined,
                                                            onRerunCheck: this._onRerunCheck,
                                                            onBypassCheck: this._onBypassCheck
                                                        };

                                                        return (
                                                            <div>
                                                                {!!checkProps.stages && checkProps.shouldShowChecksPanel && (
                                                                    <WrappedComponent
                                                                        dependencies={["ms.vss-build-web.approvals-panel"]}
                                                                        wrappedType={useUpdatedChecksFlow ? "ms.tfs-build.stage-checks-panel": "ms.tfs-build.approvals-list-panel"}
                                                                        {...checkPanelProps}
                                                                    />
                                                                )}
                                                            </div>
                                                        );
                                                    }}
                                                </Observer>
                                                <RunSummary
                                                    testsTabUrl={this._getTestsTabUrl(props.providers)}
                                                    coverageTabUrl={this._getCoverageTabUrl(props.providers)}
                                                />
                                                <IssuesCard />
                                                <ChecksCard
                                                    stages={props.run.stages}
                                                    pageContext={this.context.pageContext}
                                                    projectId={this._projectId}
                                                    onReviewClicked={this._onReviewClicked}
                                                    useUpdatedChecksFormat={useUpdatedChecksFlow}
                                                    isChecksLogsDisabled={isChecksLogsDisabled}
                                                    isAsyncCheckLogsFlowEnabled={isAsyncCheckLogsFlowEnabled}
                                                />
                                                <JobsCard
                                                    onReviewStageClicked={this._onReviewStageClicked}
                                                    onReviewManualValidationClicked={this._onReviewManualValidationClicked}
                                                />
                                                <SourcesCard />
                                            </div>
                                        );
                                    }}
                                </Observer>
                            )}
                        </TabContent>
                    </div>
                </Page>
                <TagsDialog getRemoteTags={this._getTags} onSaveTags={this._onSaveTags} run={this._run} showTagsDialog={this.state.showTagsDialog} context={this.context} />
            </Surface>
        );
    }

    private _onBypassCheck = (checkId: string, checkSuiteId: string): Promise<void> => {
        const checkBypassRequest = {
            action: UpdateAction.Bypass,
            checkId: checkId
        } as CheckSuiteUpdateParameter;

        return this._service
            .updateCheckSuite(checkSuiteId, checkBypassRequest)
            .then(
                (checks: CheckSuite) => {
                    if (checks){
                        this._service.refreshData(true);
                        this._showChecksPanel.value = false;
                        return Promise.resolve();
                    }
                },
                (error: any) => {
                    return Promise.reject(error);
                }
            );
    }

    public async componentWillMount() {
        const _source = new ContributionSource({
          pageContext: this.context.pageContext
        });
        const projectSettingData = await _source.getDataAsync<ProjectRetentionSettingData>(RetentionSettingDataContributionId, undefined, true);
        if(projectSettingData && projectSettingData.runsToRetainPerProtectedBranch) {
            this.setState({
                runsToRetainPerProtectedBranch:  projectSettingData.runsToRetainPerProtectedBranch.value.toString()
            });
        }
    };

    public componentDidMount() {
        prefetch(this.context.pageContext);
        if (!this._run) {
            return;
        }

        this._pivot.subscribe(this._onViewStateChanged);
        this._service.isDeleted.subscribe(this._onRunDeleted);
        this._run.subscribe(this._onRunUpdated);
        registerErrorHandler((error: Error) => {
            if (error) {
                this._globalMessageService.addBanner({
                    message: error.message,
                    level: 2
                });
            }
        });
        this._service.startListening();

        this._updateHeaderBreadcrumb();

        // see bug #1532544, this isn't correct since we are using tableBreakpoints in JobTable, we don't have a suggestion yet for this...
        this.context.pageContext
            .getService<IVssPerformanceService>("IVssPerformanceService")
            .markTimeToInteractive(Telemetry.Area, Telemetry.RunDetails);

        document.title = format(Resources.TitleSummary, this._run.value.number);
        this._pageHistoryService.registerObservable(this._pivot, "view", HistoryBehavior.newEntry);
        this._staticActions.subscribe(this._onActionsUpdate);
    }

    public componentWillUnmount() {
        this._pivot.unsubscribe(this._onViewStateChanged);
        this._service.isDeleted.unsubscribe(this._onRunDeleted);
        this._run.unsubscribe(this._onRunUpdated);
        registerErrorHandler((error: Error) => {});
        this._service.stopListening();
        const breadcrumbService = this.context.pageContext.getService<RunHeaderBreadcrumbService>("run-header-breadcrumb-service");
        breadcrumbService.clearBreadcrumbs();
        this._staticActions.unsubscribe(this._onActionsUpdate);
        this._contributedActions && this._contributedActions.unsubscribe(this._onActionsUpdate);
        clearTreeStateOnUnmount(this.context.pageContext);
        this._pageHistoryService.unregisterObservable("view");
    }

    // NB: for purposes of formatting this message, one month === 30 days
    //
    // > 100 years: Retained forever.
    // > 730 days: Retained for more than 2 years
    // > 365 days: Retained for more than 1 year, n month(s)
    // > 180 days: Retained for more than n months
    // > 30 days: Retained for n month(s), m day(s)
    // <= 30 days: Retained for n day(s)
    private _getRetentionMessage = (run: RunDetailsData, leases: RetentionLeaseData[], runsToRetainPerProtectedBranch: string): string => {
        let daysToExpiration = run.retainedFor;

        const maxLease = this._getMaxLease(leases, run.retainedFor);
        if (maxLease) {
            daysToExpiration = this._getDaysUntilExpiration(maxLease);
        }

        function moreThanOrExact(years: number, months: number): string {
            return years > 0 || months >= 6 ? Resources.RetainedByLeaseBegin_MoreThan : Resources.RetainedByLeaseBegin_Exact;
        }

        function years(years: number): string {
            switch (years) {
                case 0:
                    return "";
                case 1:
                    return Resources.RetainedByLeaseForOneYear;
                default:
                    return format(Resources.RetainedByLeaseForYears, years);
            }
        }

        function months(months: number): string {
            switch (months) {
                case 0:
                    return "";
                case 1:
                    return Resources.RetainedByLeaseForOneMonth;
                default:
                    return format(Resources.RetainedByLeaseForMonths, months);
            }
        }

        function days(days: number): string {
            switch (days) {
                case 0:
                    return "";
                case 1:
                    return Resources.RetainedByLeaseForOneDay;
                default:
                    return format(Resources.RetainedByLeaseForDays, days);
            }
        }

        // 100 years early out
        // you need a lease for a value this large
        if (daysToExpiration >= 36500) {
            switch (maxLease!.leaseType) {
                case LeaseType.Pipeline:
                    return format(Resources.RetainedByPipelineRecentRuns, runsToRetainPerProtectedBranch);
                case LeaseType.Branch:
                    return format(Resources.RetainedByProtectBranchRecentRuns, runsToRetainPerProtectedBranch, maxLease!.ownerDisplayName);
                case LeaseType.User:
                case LeaseType.ReleaseManagment:
                default:
                    return format(Resources.RetainedByLeaseForever, maxLease!.ownerDisplayName, maxLease!.leaseType);
            }
        }

        const yearsToExpiration = daysToExpiration >= 365 ? Math.floor(daysToExpiration / 365) : 0;
        daysToExpiration -= yearsToExpiration * 365;

        const monthsToExpiration = Math.floor(daysToExpiration / 30);
        daysToExpiration -= monthsToExpiration * 365;

        if (daysToExpiration < 0) {
            daysToExpiration = 0;
        }

        let result = years(yearsToExpiration);

        if (yearsToExpiration < 2) {
            const monthsString = months(monthsToExpiration);

            if (yearsToExpiration === 1) {
                if (monthsToExpiration > 0) {
                    result += ", ";
                }
            }

            if (monthsToExpiration > 0) {
                result += monthsString;
            }

            if (yearsToExpiration === 0) {
                if (monthsToExpiration < 6) {
                    const daysString = days(daysToExpiration);

                    if (monthsToExpiration > 0 && daysToExpiration > 0) {
                        result += ", ";
                    }

                    if (daysToExpiration > 0) {
                        result += daysString;
                    }
                }
            }
        }

        if (maxLease) {
            return (
                moreThanOrExact(yearsToExpiration, monthsToExpiration) +
                result +
                format(Resources.RetainedByLeaseEnd, maxLease.ownerDisplayName, maxLease.leaseType)
            );
        }

        if (run.retainedFor > 1) {
            return format(Resources.RetainedByNobody, result);
        }

        return Resources.RetainedByNobodyNextRun;
    };

    private _getMaxLease = (leases: RetentionLeaseData[], minimumRetentionTime: number): RetentionLeaseData | undefined => {
        if (leases.length > 0) {
            let longestLease = leases[0];
            let longestLeaseDays = this._getDaysUntilExpiration(longestLease);
            for (let i = 1; i !== leases.length; ++i) {
                const check = this._getDaysUntilExpiration(leases[i]);
                if (check > longestLeaseDays) {
                    longestLease = leases[i];
                    longestLeaseDays = check;
                }
            }

            if (longestLeaseDays >= minimumRetentionTime) {
                return longestLease;
            }
        }

        return undefined;
    };

    private _getDaysUntilExpiration = (lease: RetentionLeaseData): number => {
        return Math.floor((lease.validUntil.getTime() - Date.now()) / (1000 * 3600 * 24));
    };

    private _getMaxDaysUntilExpiration = (run: RunDetailsData, leases: RetentionLeaseData[]): number => {
        if (leases.length > 0) {
            const leaseExpiration = leases.reduce((previous, current) => {
                return Math.max(current.validUntil.getTime(), previous);
            }, 0);
            return Math.floor((leaseExpiration - Date.now()) / (1000 * 3600 * 24));
        }

        return run.retainedFor;
    };

    private _updateTab = (tab: IVssContributedTab) => {
        if (tab.id === c_testTabId) {
            tab.url = this._navigationService.getLinkingService().getBuildUrl(this._run.value.id, NavTestTabId);
            tab.id = NavTestTabId;
        } else {
            const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
            tab.url = historyService.generateUrl({ view: tab.id });
        }
    };

    private _getLegacyConfig() {
        return {
            onBuildChanged: (handler: BuildCallbackType) => {
                this._extCallbacks.push(handler);
                const latestBuild = this._getBuild();
                latestBuild && latestBuild.then(x => handler(x));
            },
            onViewDisplayed: legacyNoop,
            selectTab: legacyNoop,
            setSectionVisibility: legacyNoop
        } as IBuildResultsViewExtensionConfig;
    }

    private _getCoverageTabUrl(providers: IVssContributedTab[]) {
        if (providers && providers.length > 0) {
            const coverageTab = providers.filter(tab => tab.id === CodeCoverageCiTabId);
            if (coverageTab && coverageTab.length > 0 && coverageTab[0].id) {
                return this._navigationService.getLinkingService().getBuildUrl(this._run.value.id, coverageTab[0].id);
            }
        }

        return "";
    }

    private _getTestsTabUrl(providers: IVssContributedTab[]) {
        if (providers && providers.length > 0) {
            const testsTab = providers.filter(tab => tab.id === NavTestTabId);
            if (testsTab && testsTab.length > 0 && testsTab[0].id) {
                return this._navigationService.getLinkingService().getBuildUrl(this._run.value.id, testsTab[0].id);
            }
        }

        return "";
    }

    private _getBuild() {
        if (this._run) {
            const buildClient = this.context.pageContext.getRestClient<IBuildRestClient>("IBuildRestClient");
            return buildClient.getBuild(this._projectId, this._run.value.id);
        }
    }

    private _getHeaderIconProps = (run: RunDetailsData): IIconProps => {
        const status = getBuildStatusIndicatorData(run.status, run.result);
        return {
            render: () => {
                return (
                    <Tooltip text={status.label}>
                        <span>
                            <Status ariaLabel={status.label} size={StatusSize.l} {...status.statusProps} />
                        </span>
                    </Tooltip>
                );
            }
        };
    };

    private async _initializeActions() {
        const actions: IHeaderCommandBarItem[] = [];
        const run = this._run && this._run.value;

        if (run) {
            const permissions = this._service.getPermissions();
            this._addRetryActionItems(actions, run, permissions);
            this._addRunActionItems(actions, run, permissions);
            this._addCancelActionItems(actions, run, permissions);
            this._addLogsActionItems(actions, run);
            this._addTagsActionItems(actions, run, permissions);
            this._addEditActionItems(actions, run, permissions);
            this._addRetainActionItems(actions, run, permissions);
            this._addDeleteActionItems(actions, run, permissions);
        }

        this._staticActions.value = actions;

        if (run) {
            if (this._contributedActions) {
                this._contributedActions.unsubscribe(this._onActionsUpdate);
                this._contributedActions = new ObservableCollection<IHeaderCommandBarItem>();
            }
            this._contributedActions.push(
                getMenuItems(this.context.pageContext, "ms.vss-build-web.run-actions", {
                    runId: run.id,
                    result: run.result,
                    pipelineId: run.pipeline.id,
                    status: run.status,
                    projectId: this._projectId
                } as IRunActionProps)
            );
            if (run.status == BuildStatus.Completed) {
                const build = await this._getBuild();
                this._contributedActions.push(getMenuItems(this.context.pageContext, "ms.vss-build-web.build-result-completed-actions-menu", build));
            }

            this._contributedActions.push(
                getMenuItems(this.context.pageContext, "ms.vss-build-web.build-result-actions-menu", this._getLegacyConfig())
            );
            this._contributedActions.subscribe(this._onActionsUpdate);
        }

        this._onActionsUpdate();
    }

    private _onActionsUpdate = () => {
        const contributedActions = this._contributedActions ? this._contributedActions.value : [];
        if (contributedActions.length > 0) {
            contributedActions.unshift({
                id: "contributed-divider",
                important: false,
                itemType: MenuItemType.Divider
            });
        }

        // make sure no contributed one shows up as important
        contributedActions.forEach(x => (x.important = false));

        this._headerActions.value = this._staticActions.value.concat(contributedActions);
    };

    private _addRunActionItems(actions: IHeaderCommandBarItem[], run: RunDetailsData, permissions: IRunDetailsPermissions) {
        const isDisabled = run.pipeline.queueStatus === DefinitionQueueStatus.Disabled;
        const buildClient = this.context.pageContext.getRestClient<IBuildRestClient>("IBuildRestClient");

        if (run) {
            if (permissions.canRunBuild && !isDisabled && run.status !== BuildStatus.InProgress && run.status !== BuildStatus.NotStarted) {
                actions.push({
                    id: "run-pipeline-command",
                    text: Resources.RunPipeline,
                    important: true,
                    isPrimary: true,
                    onActivate: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                        buildClient.getDefinition(this._projectId, run.pipeline.id).then((retrievedDefinition: BuildDefinition) => {
                            const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                            // use the same branch as the current build for non tfvc repos
                            const istfvc =
                                retrievedDefinition.repository &&
                                retrievedDefinition.repository.type &&
                                retrievedDefinition.repository.type.toLowerCase() === RepositoryTypes.TfsVersionControl.toLowerCase();
                            let sourceBranch =
                                !istfvc && this._run && this._run.value && this._run.value.sourceBranch ? this._run.value.sourceBranch : undefined;
                            if (sourceBranch === undefined) {
                                sourceBranch = retrievedDefinition.repository ? retrievedDefinition.repository.defaultBranch : undefined;
                            }
                            layoutManager.renderCallout(onDismiss => {
                                const props: IQueueBuildProps = {
                                    definition: retrievedDefinition,
                                    repository: retrievedDefinition.repository,
                                    processType: retrievedDefinition.process.type,
                                    pageContext: this.context.pageContext,
                                    project: retrievedDefinition.project.id,
                                    source: "runs",
                                    onSuccess: (queuedBuild: Build) => {
                                        // The user will be redirected to the new run if the run panel is on, but for now just using message bar to give user feedback
                                        const globalMessageService =
                                            this.context.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
                                        globalMessageService.addBanner({
                                            message: format(Resources.RunQueued, queuedBuild.buildNumber),
                                            level: GlobalMessageLevel.success
                                        });
                                    },
                                    definitionVariables: retrievedDefinition.variables,
                                    previousRunVariables: this._deserializeVariableData(run.parameters),
                                    previousTemplateParameters: run.templateParameters,
                                    queue: retrievedDefinition.queue,
                                    defaultSourceBranch: sourceBranch,
                                    onDismiss
                                };

                                return React.createElement(
                                    CommandComponent,
                                    {
                                        commandType: "ciQueueBuildComponent",
                                        ...props
                                    },
                                    null
                                );
                            });
                        }, handleError);
                    }
                });
            }
        }
    }

    private _addRetryActionItems(actions: IHeaderCommandBarItem[], run: RunDetailsData, permissions: IRunDetailsPermissions) {
        const isDisabled = !run.pipeline || run.pipeline.queueStatus === DefinitionQueueStatus.Disabled;

        if (run) {
            if (
                permissions.canRunBuild &&
                !isDisabled &&
                run.status !== BuildStatus.InProgress &&
                run.status !== BuildStatus.NotStarted &&
                run.result != BuildResult.Succeeded &&
                run.result != BuildResult.PartiallySucceeded
            ) {
                actions.push({
                    id: "retry-pipeline-command",
                    text: Resources.RetryFromFailed,
                    important: true,
                    isPrimary: false,
                    onActivate: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                        this._confirmAction(Resources.RerunFromFailed, Resources.ConfirmRerunPipeline, () => {
                            this._service.retryPipeline();
                        });
                    }
                });
            }
        }
    }

    private _addCancelActionItems(actions: IHeaderCommandBarItem[], run: RunDetailsData, permissions: IRunDetailsPermissions) {
        if (permissions.canCancelBuild && (run.status === BuildStatus.InProgress || run.status === BuildStatus.NotStarted)) {
            let name = "";
            if (this.state.cancelClicked) {
                name = Resources.CancelingBuild;
            } else {
                name = Resources.CancelBuild;
            }

            actions.push({
                id: c_cancelBuildAction,
                text: name,
                disabled: this.state.cancelClicked,
                important: true,
                onActivate: () => {
                    const confirmationText = format(Resources.ConfirmCancelBuild, run.number);
                    this._confirmAction(Resources.CancelBuild, confirmationText, () => {
                        this.setState({
                            cancelClicked: true
                        });
                        this._service.cancelRun();
                    });
                }
            });
        }
    }

    private _addLogsActionItems(actions: IHeaderCommandBarItem[], run: RunDetailsData) {
        if (run.status === BuildStatus.Completed) {
            const initiateDownload = run.logs && run.logs.type.toLowerCase() === ArtifactResourceTypes.Container;
            // todo: generate url client side to speed up data provider
            // const url = routeUrl(this.context.pageContext, "ms.vss-build-web.run-build-logsapi-route", { project: this._projectId, buildId: run.id + "", logId: run.logs.id + "" });
            actions.push({
                id: "download-logs-command",
                text: initiateDownload || !run.logs ? Resources.DownloadAllLogs : Resources.CopyLogsLocation,
                important: false,
                onActivate: () => {
                    if (run.logs) {
                        if (initiateDownload) {
                            openLinkSecurely(run.logs.url + "?$format=zip");
                        } else {
                            if (document.queryCommandSupported("copy")) {
                                // let the user copy the URL
                                const textArea = document.createElement("textarea");
                                textArea.value = run.logs.url;
                                document.body.appendChild(textArea);
                                textArea.focus();
                                textArea.select();
                                document.execCommand("copy");
                                document.body.removeChild(textArea);
                            } else {
                                console.error("Browser doesn't allow to copy URL " + run.logs.url);
                            }
                        }
                    } else {
                        const toastService = this.context.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
                        toastService.addToast({
                            duration: 2000,
                            message: Resources.NoLogsToastMessage
                        });
                    }
                }
            });

            actions.push({
                id: "download-command-divider",
                important: false,
                itemType: MenuItemType.Divider
            });
        }
    }

    private _addTagsActionItems(actions: IHeaderCommandBarItem[], run: RunDetailsData, permissions: IRunDetailsPermissions) {
        if (permissions.canEditTags) {
            actions.push({
                id: c_tagBuildAction,
                text: run.tags.length === 0 ? Resources.AddTags : Resources.EditTags,
                important: false,
                onActivate: () => {
                    this.state.showTagsDialog.value = true;
                }
            });
        }
    }

    private _addEditActionItems(actions: IHeaderCommandBarItem[], run: RunDetailsData, permissions: IRunDetailsPermissions) {
        const linkingService = this._navigationService.getLinkingService();
        if (linkingService && run.pipeline) {
            const isYaml = run.processType === ProcessType.Yaml;
            const isPR = run.reason === BuildReason.PullRequest;
            let branch = "";
            // Note: we are ignoring PR's for now, ideally we would want to get the target branch of PR and send it here, #1553932 for that
            if (!isPR && isYaml) {
                const sourceProviderService = this.context.pageContext.getService<ISourceProviderService>("ISourceProviderService");
                const sourceProvider = sourceProviderService.getSourceProvider(run.sourceRepositoryType);
                branch = sourceProvider ? sourceProvider.getBranchText(run.sourceBranch) : "";
            }

            const editLink = isYaml
                ? linkingService.getYamlPipelineEditUrl(run.pipeline.id, branch)
                : linkingService.getDefinitionEditUrl(run.pipeline.id);
            actions.push({
                id: "edit-pipeline-command",
                text: permissions.canEditPipeline ? Resources.EditPipeline : Resources.ViewPipeline,
                important: false,
                href: editLink,
                onActivate: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                    onClickFPS(this.context.pageContext, editLink, true, event);
                }
            });
        }
    }

    private _addRetainActionItems(actions: IHeaderCommandBarItem[], run: RunDetailsData, permissions: IRunDetailsPermissions) {
        if (!run.deleted) {
            actions.push({
                id: "retain-command-divider",
                important: false,
                itemType: MenuItemType.Divider
            });

            actions.push({
                id: "open-retention-panel-command",
                important: false,
                onActivate: () => this._onActivateRetentionPanel(permissions.canRetainBuild),
                text: Resources.ViewRetentionLeasesMenuItem
            });

            if (permissions.canRetainBuild) {
                const vssPageService = this.context.pageContext.getService<IVssPageService>("IVssPageService");
                const vssPageData = vssPageService.getData();

                if (vssPageData) {
                    const runIsRetainedByUser = run.retentionLeases.some(
                        lease => lease.leaseOwnerIdentityRef && lease.leaseOwnerIdentityRef.id === vssPageData.user.id
                    );

                    if (runIsRetainedByUser) {
                        actions.push({
                            text: Resources.StopRetainingBuild,
                            id: "run-action-retain",
                            important: false,
                            onActivate: () => {
                                this._service.stopRetainingBuild();
                            }
                        });
                    } else {
                        actions.push({
                            text: Resources.RetainBuild,
                            id: "run-action-retain",
                            important: false,
                            onActivate: () => {
                                this._service.retainBuild();
                            }
                        });
                    }
                }
            }
        }
    }

    private _addDeleteActionItems(actions: IHeaderCommandBarItem[], run: RunDetailsData, permissions: IRunDetailsPermissions) {
        // Delete action
        if (run.retentionLeases.length === 0 && run.pipeline && !run.deleted && permissions.canDeleteBuild) {
            actions.push({
                id: c_deleteBuildAction,
                text: Resources.DeleteRunActionName,
                important: false,
                onActivate: () => {
                    const confirmationText = format(Resources.ConfirmDeleteRun, run.number);
                    this._confirmAction(Resources.DeleteRun, confirmationText, () => {
                        this._service.deleteRun();
                    });
                }
            });
        }
    }

    private _confirmAction = (title: string, description: string, callback: () => any) => {
        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(onDismiss => {
            const props: IConfirmPipelineActionProps = {
                title: title,
                message: description,
                onClose: onDismiss,
                onConfirm: callback
            };

            return React.createElement(
                CommandComponent,
                {
                    commandType: "ciDConfirmPipelineComponent",
                    ...props
                },
                null
            );
        });
    };

    private _getTags = (): Promise<string[]> => {
        return this._service.getTags();
    };

    private _onClickHeaderBack = () => {
        const linkingService = this.context.pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
        const pipelinePageLink = linkingService.getPipelinesUrl(this._run.value.pipeline.id);
        onClickFPS(this.context.pageContext, pipelinePageLink, true);
    };

    private _onTabChanged = (tabKey: string) => {
        this._pivot.value = tabKey;
        if (tabKey === NavTestTabId) {
            this._performanceService.startScenario(c_testTabId, false, undefined, { isTabChangeScenario: true });
        } else if (tabKey === CodeCoverageCiTabId) {
            this._performanceService.startScenario(c_coverageCiTabPerfScenario, false, undefined, { isTabChangeScenario: true });
        }
    };

    private _onViewStateChanged = (selectedPivot: string) => {
        this.setState({
            selectedTab: selectedPivot
        });
    };

    private _onRunDeleted = (isDeleted: boolean) => {
        if (isDeleted) {
            const url = this._navigationService.getLinkingService().getDefinitionUrl(this._run.value.pipeline.id);
            onClickFPS(this.context.pageContext, url, true);
        }
    };

    private _onSubmitBulkApproval = (approvalUpdates: ApprovalUpdateParameters[]) => {
        this._service
            .updateApprovals(approvalUpdates)
            .then(approvals => {
                if (approvals) {
                    this._service.refreshData(true);
                    this._showChecksPanel.value = false;
                }
            })
            .catch(handleError);
    };

    private _onReassignApprovals = (approvalUpdates: ApprovalUpdateParameters[]): Promise<void> => {
        return this._service.updateApprovals(approvalUpdates).then(
            approvals => {
                if (approvals) {
                    this._service.refreshData(true);
                    this._showChecksPanel.value = false;

                    // Display toast in reassign scenario
                    this._globalMessageService.addToast({
                        message: Resources.ApprovalReassigned,
                        duration: 5000,
                        forceOverrideExisting: true
                    });
                    return Promise.resolve();
                }
            },
            error => {
                return Promise.reject(error);
            }
        );
    };

    private _onPermitResource = (permissionsRequest: ResourcePipelinePermissions, resourceType: string, resourceId: string) => {
        this._service.updatePermissions(permissionsRequest, resourceType, resourceId).then(permissions => {
            if (permissions) {
                this._globalMessageService.addToast({
                    message: Resources.PermissionGranted,
                    duration: 5000,
                    forceOverrideExisting: true
                });
                this._service.refreshData(true);
            }
        });
    };

    private _onRerunCheck = (checkId: string, checksuiteId: string): Promise<void> => {
        const checkRerunRequest = {
            action: UpdateAction.Rerun,
            checkId: checkId
        } as CheckSuiteUpdateParameter;

        return this._service
            .updateCheckSuite(checksuiteId, checkRerunRequest)
            .then(
            (checks: CheckSuite) => {
                if (checks) {
                    this._service.refreshData(true);
                    this._showChecksPanel.value = false;
                    return Promise.resolve();
                }
            },
            (error: any) => {
                return Promise.reject(error);
            }
        );
    };

    private _shouldHideChecksPanel = (stages: RunStageData[]) => {
        if (this._reviewMode.value === ReviewMode.Stage && this._stagesToReview.value && this._stagesToReview.value.length === 1) {
            const currentStage = stages.find(stage => stage.id === this._stagesToReview.value[0].id);
            if (!(currentStage && currentStage.checks && currentStage.checks.some(check => check.status === CheckRunStatus.Running))) {
                this._showChecksPanel.value = false;
            }
        } else if (!stages.some(stage => stage.checks && stage.checks.some(check => check.status === CheckRunStatus.Running))) {
            this._showChecksPanel.value = false;
        }
    };

    private _updateHeaderBreadcrumb() {
        const linkingService = this.context.pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
        const pipelineName = this._run.value.pipeline.name;
        const pipelineUrl = linkingService.getDefinitionUrl(this._run.value.pipeline.id);
        const runNumber = this._run.value.number;
        const runUrl = linkingService.getBuildUrl(this._run.value.id);

        const breadcrumbService = this.context.pageContext.getService<RunHeaderBreadcrumbService>("run-header-breadcrumb-service");
        breadcrumbService.setBreadcrumbs(pipelineName, pipelineUrl, runNumber, runUrl);
    }

    private _onActivateRetentionPanel = (canRetainBuild: boolean) => {
        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(onDismiss => {
            const observableRetentionLeases = this._service.retentionLeases;
            const props: RetentionPanelProps = {
                canDeleteLeases: canRetainBuild,
                onDelete: (ids: number[]) => {
                    this._service.deleteRetentionLeases(ids);

                    // Optimistically update observable to remove deleted item from displayed list
                    ids.forEach((leaseId: number) => {
                        const indexToRemove = observableRetentionLeases.value.findIndex((lease: RetentionLeaseData) => lease.id === leaseId);
                        observableRetentionLeases.splice(indexToRemove, 1);
                    });
                },
                onDismiss: onDismiss,
                retentionLeases: observableRetentionLeases
            };

            return React.createElement(RetentionPanel, props);
        });
    };

    private _onReviewStageClicked = (stage: RunStageData) => {
        this._stagesToReview.value = [stage as IStageInfo];
        this._reviewMode.value = ReviewMode.Stage;
        this._reviewingPendingStages = false;
        this._showChecksPanel.value = true;
        this._checkListItemType.value = CheckListItemType.Checks;
        this._activeElement = document.activeElement as HTMLElement;
    };

    private _onReviewManualValidationClicked = (stage: RunStageData) => {
        this._stagesToReview.value = [stage as IStageInfo];
        this._reviewMode.value = ReviewMode.Stage;
        this._showChecksPanel.value = true;
        this._checkListItemType.value = CheckListItemType.ManualValidation;
        this._activeElement = document.activeElement as HTMLElement;
    };

    private _onReviewClicked = (stages: IStageCheckData[]) => {
        this._stagesToReview.value = stages;
        this._pendingStages = stages;
        this._reviewMode.value = ReviewMode.AllPending;
        this._reviewingPendingStages = true;
        this._showChecksPanel.value = true;
        this._checkListItemType.value = CheckListItemType.All;
        this._activeElement = document.activeElement as HTMLElement;
    };

    private _onViewStageChecksClicked = (stage: IStageInfo) => {
        this._stagesToReview.value = [stage];
        this._reviewMode.value = ReviewMode.Stage;
        this._showChecksPanel.value = true;
        this._checkListItemType.value = CheckListItemType.Checks;
        this._activeElement = document.activeElement as HTMLElement;
    };

    private _onStageChecksBackClicked = () => {
        this._stagesToReview.value = this._pendingStages;
        this._reviewMode.value = ReviewMode.AllPending;
        this._showChecksPanel.value = true;
        this._checkListItemType.value = CheckListItemType.All;
        this._activeElement = document.activeElement as HTMLElement;
    };

    private _onPanelDismiss = () => {
        this._showChecksPanel.value = false;
        if (this._activeElement) {
            this._activeElement.focus();
            this._activeElement = undefined;
        }
    };

    private _onRunUpdated = () => {
        let breadcrumbValuesUpdated = false;
        const updatedRun = this._run.value;
        let invokeLegacyCallback = false;

        //  update the actions first so that we can re-render once when the contributedTabProps are updated
        this._initializeActions();

        if (updatedRun.id != this._contributedTabProps.runId.value) {
            this._contributedTabProps.runId.value = updatedRun.id;
            breadcrumbValuesUpdated = true;
            invokeLegacyCallback = true;
        }
        if (updatedRun.number != this._contributedTabProps.runNumber.value) {
            this._contributedTabProps.runNumber.value = updatedRun.number;
            breadcrumbValuesUpdated = true;
        }
        if (updatedRun.status != this._contributedTabProps.status.value) {
            this._contributedTabProps.status.value = updatedRun.status;
            invokeLegacyCallback = true;
        }
        if (updatedRun.sourceBranch != this._contributedTabProps.sourceBranch.value) {
            this._contributedTabProps.sourceBranch.value = updatedRun.sourceBranch;
        }
        if (updatedRun.sourceRepositoryId != this._contributedTabProps.sourceRepositoryId.value) {
            this._contributedTabProps.sourceRepositoryId.value = updatedRun.sourceRepositoryId;
        }
        if (updatedRun.sourceRepositoryType != this._contributedTabProps.sourceRepositoryType.value) {
            this._contributedTabProps.sourceRepositoryType.value = updatedRun.sourceRepositoryType;
        }
        if (updatedRun.pipeline.id != this._contributedTabProps.pipelineId.value) {
            this._contributedTabProps.pipelineId.value = updatedRun.pipeline.id;
        }
        if (updatedRun.sourceVersion != this._contributedTabProps.sourceVersion.value) {
            this._contributedTabProps.sourceVersion.value = updatedRun.sourceVersion;
        }
        if (updatedRun.finishTime != this._contributedTabProps.finishTime.value) {
            this._contributedTabProps.finishTime.value = updatedRun.finishTime;
        }

        this._contributedTabProps.notifyUpdated.value++;

        if (breadcrumbValuesUpdated) {
            this._updateHeaderBreadcrumb();
        }

        if (invokeLegacyCallback) {
            const latestBuild = this._getBuild();
            if (latestBuild) {
                latestBuild.then(build => {
                    this._extCallbacks.forEach(x => {
                        x(build);
                    });
                });
            }
        }

        this._shouldHideChecksPanel(this._run.value.stages);
    };

    private _onSaveTags = async (newTagsSet: string[]): Promise<void> => {
        await this._service.updateTags(newTagsSet);
    };

    private _deserializeVariableData(variableJson: string): { [key: string]: string } {
        let variables: { [key: string]: string };
        try {
            variables = JSON.parse(variableJson);
        } catch {
            variables = {};
        }
        return variables;
    }
}

Components.add("run-view-content", RunViewContent);
